import React, { useState, useEffect, useMemo } from 'react';
import { useRouter, Link } from '../../router/Router';
import { useAuth } from '../../auth/AuthContext';
import { paymentApi } from '../../services/paymentApi';
import { adminBookingService } from '../../services/adminBookingService';
import { PaymentAttempt, Trip, Booking } from '../../types';
import {
  Clock,
  Calendar,
  Ticket,
  CheckCircle2,
  Bus,
  ArrowLeft,
  CreditCard,
  Building2,
  Phone,
  RefreshCw,
} from 'lucide-react';

interface AdminDashboardProps {
  pendingCount: number;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ pendingCount: propPendingCount }) => {
  const { navigate } = useRouter();
  const { isOperator, staffProfile } = useAuth();
  const [recentAttempts, setRecentAttempts] = useState<PaymentAttempt[]>([]);
  const [allTrips, setAllTrips] = useState<Trip[]>([]);
  const [allBookings, setAllBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [livePendingCount, setLivePendingCount] = useState<number>(propPendingCount);

  const loadDashboardData = async (forceRefresh = false) => {
    setIsLoading(true);
    try {
      const [pendingRes, trips, bookings] = await Promise.all([
        !isOperator ? paymentApi.getPendingManualPayments(forceRefresh) : Promise.resolve({ success: true, data: [] }),
        adminBookingService.getAllTrips(forceRefresh),
        adminBookingService.getAllBookings(forceRefresh),
      ]);

      if (pendingRes.success && pendingRes.data) {
        setRecentAttempts(pendingRes.data.slice(0, 5));
        setLivePendingCount(pendingRes.data.length);
      }
      setAllTrips(trips);
      setAllBookings(bookings);
    } catch (err) {
      console.warn('Could not fetch dashboard live data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadDashboardData();
    const handleSync = () => {
      loadDashboardData(true);
    };
    window.addEventListener('oto_data_sync', handleSync);
    window.addEventListener('oto_booking_deleted', handleSync);
    return () => {
      window.removeEventListener('oto_data_sync', handleSync);
      window.removeEventListener('oto_booking_deleted', handleSync);
    };
  }, [isOperator]);

  // Update livePendingCount when prop changes if greater
  useEffect(() => {
    if (propPendingCount !== undefined && propPendingCount !== livePendingCount) {
      setLivePendingCount(propPendingCount);
    }
  }, [propPendingCount]);

  // Today's Date in Egypt timezone (YYYY-MM-DD)
  const todayStr = useMemo(() => {
    try {
      return new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo' }).format(new Date());
    } catch {
      return new Date().toISOString().split('T')[0];
    }
  }, []);

  // Compute live real metrics
  const todayTrips = useMemo(() => {
    return allTrips.filter((t) => t.date === todayStr && t.status !== 'CANCELLED');
  }, [allTrips, todayStr]);

  const todayBookings = useMemo(() => {
    return allBookings.filter(
      (b) =>
        (b.createdAt && b.createdAt.startsWith(todayStr)) ||
        b.tripDate === todayStr
    );
  }, [allBookings, todayStr]);

  const confirmedBookings = useMemo(() => {
    return allBookings.filter(
      (b) => b.status === 'CONFIRMED' || b.paymentStatus === 'PAID'
    );
  }, [allBookings]);

  const { availableSeatsCount, openTripsCount } = useMemo(() => {
    const openTrips = allTrips.filter(
      (t) => (t.date >= todayStr || !t.date) && t.status === 'OPEN'
    );
    const seats = openTrips.reduce(
      (acc, t) => acc + (typeof t.availableSeats === 'number' ? t.availableSeats : (t.capacity || 50)),
      0
    );
    return { availableSeatsCount: seats, openTripsCount: openTrips.length };
  }, [allTrips, todayStr]);

  const effectivePendingCount = Math.max(livePendingCount, propPendingCount || 0);

  return (
    <div className="admin-dashboard-view" dir="rtl">
      {/* Welcome Banner */}
      <div className="admin-overview-header">
        <div>
          <span
            className="admin-role-badge"
            style={{
              background: isOperator ? '#E0F2FE' : undefined,
              color: isOperator ? '#0369A1' : undefined,
            }}
          >
            {isOperator ? 'لوحة المتابعة والتشغيل اليومي' : 'لوحة التحكم الإدارية المركزية'}
          </span>
          <h2 className="admin-overview-title">
            {isOperator
              ? `مرحباً، ${staffProfile?.fullName || 'طاقم تشغيل منظومة OTO'}`
              : 'مرحباً، إدارة منظومة OTO STUDENTS للطلاب'}
          </h2>
          <p className="admin-overview-sub">
            {isOperator
              ? 'متابعة تشغيل رحلات طلاب الإسكندرية، إدارة الحجوزات والعمليات، وجداول المواعيد والأسطول.'
              : 'متابعة تشغيل رحلات طلاب الإسكندرية، اعتماد تحويلات إنستاباي والمحافظ، وإدارة الأسطول والمقاعد.'}
          </p>
        </div>

        <div className="admin-header-actions">
          <button
            onClick={() => loadDashboardData(true)}
            disabled={isLoading}
            className="btn-admin-action-secondary flex items-center gap-1.5"
            title="تحديث البيانات لحظياً"
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            <span>تحديث</span>
          </button>
          {!isOperator ? (
            <Link to="/admin/payments" className="btn-admin-action-primary flex items-center gap-2">
              <Clock size={18} />
              <span>مراجعة المدفوعات المعلقة</span>
              {effectivePendingCount > 0 && <span className="kpi-bubble">{effectivePendingCount}</span>}
            </Link>
          ) : (
            <Link to="/admin/bookings" className="btn-admin-action-primary flex items-center gap-2">
              <Ticket size={18} />
              <span>سجل حجوزات اليوم</span>
              {todayBookings.length > 0 && <span className="kpi-bubble">{todayBookings.length}</span>}
            </Link>
          )}
          <Link to="/student" className="btn-admin-action-secondary flex items-center gap-1.5">
            <span>معاينة بوابة الطالب</span>
            <ArrowLeft size={16} />
          </Link>
        </div>
      </div>

      {/* KPIs Grid (Live Real-Time Data from Firestore) */}
      <div className="admin-kpi-grid">
        {/* KPI 1: Pending Payment Reviews (Super Admin Only) */}
        {!isOperator && (
          <div
            className={`admin-kpi-card highlight-kpi ${effectivePendingCount > 0 ? 'has-pending' : ''}`}
            onClick={() => navigate('/admin/payments')}
            role="button"
            tabIndex={0}
          >
            <div className="kpi-icon-box orange">
              <Clock size={24} />
            </div>
            <div className="kpi-info">
              <span className="kpi-label">تحويلات قيد المراجعة</span>
              <div className="kpi-value-row">
                <span className="kpi-number">{effectivePendingCount}</span>
                <span className="kpi-unit">طلب ينتظر الاعتماد</span>
              </div>
              <span className="kpi-action-link">
                <span>انتقل للمراجعة الفورية</span>
                <ArrowLeft size={14} />
              </span>
            </div>
          </div>
        )}

        {/* KPI 2: Today's Trips */}
        <div
          className="admin-kpi-card cursor-pointer"
          onClick={() => navigate('/admin/trips')}
          role="button"
          tabIndex={0}
        >
          <div className="kpi-icon-box" style={{ background: '#FEE2E2', color: '#DC2626' }}>
            <Calendar size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">رحلات اليوم المجدولة</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{todayTrips.length}</span>
              <span className="kpi-unit">رحلة انطلاق</span>
            </div>
            <span className="kpi-meta text-emerald-600 font-semibold">
              {todayTrips.length > 0 ? `${todayTrips.length} رحلة مجدولة لليوم` : 'لا توجد رحلات مجدولة لليوم'}
            </span>
          </div>
        </div>

        {/* KPI 3: Today's Bookings */}
        <div
          className="admin-kpi-card cursor-pointer"
          onClick={() => navigate('/admin/bookings')}
          role="button"
          tabIndex={0}
        >
          <div className="kpi-icon-box purple">
            <Ticket size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">حجوزات اليوم الجديدة</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{todayBookings.length}</span>
              <span className="kpi-unit">تذكرة مسجلة</span>
            </div>
            <span className="kpi-meta text-slate-500">
              {todayBookings.length > 0 ? `إجمالي الحجوزات المسجلة اليوم` : 'في انتظار حجوزات جديدة'}
            </span>
          </div>
        </div>

        {/* KPI 4: Confirmed Bookings */}
        <div
          className="admin-kpi-card cursor-pointer"
          onClick={() => navigate('/admin/bookings')}
          role="button"
          tabIndex={0}
        >
          <div className="kpi-icon-box green">
            <CheckCircle2 size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">الحجوزات المعتمدة (النشطة)</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{confirmedBookings.length}</span>
              <span className="kpi-unit">تذكرة مدفوعة</span>
            </div>
            <span className="kpi-meta text-emerald-600">تذاكر برمز QR جاهزة للصعود</span>
          </div>
        </div>

        {/* KPI 5: Available Seats */}
        <div
          className="admin-kpi-card cursor-pointer"
          onClick={() => navigate('/admin/trips')}
          role="button"
          tabIndex={0}
        >
          <div className="kpi-icon-box teal">
            <Bus size={24} />
          </div>
          <div className="kpi-info">
            <span className="kpi-label">المقاعد الشاغرة المتاحة</span>
            <div className="kpi-value-row">
              <span className="kpi-number">{availableSeatsCount}</span>
              <span className="kpi-unit">مقعد متاح</span>
            </div>
            <span className="kpi-meta text-slate-500">
              في {openTripsCount} رحلة مفتوحة للحجز
            </span>
          </div>
        </div>
      </div>

      {/* Main Two-Column Content */}
      <div className="admin-dashboard-columns">
        {/* Left Column */}
        <div className="admin-dash-panel flex-1">
          {!isOperator ? (
            <>
              <div className="panel-header-row">
                <div className="flex items-center gap-2">
                  <Clock size={18} className="text-[#E5232A]" />
                  <h3>طابور المدفوعات المعلقة للمراجعة</h3>
                </div>
                <Link to="/admin/payments" className="panel-link">
                  <span>عرض الكل ({effectivePendingCount})</span>
                  <ArrowLeft size={16} />
                </Link>
              </div>

              <div className="panel-body">
                {isLoading ? (
                  <p className="p-4 text-center text-slate-500 text-sm">جاري جلب الطلبات...</p>
                ) : recentAttempts.length === 0 ? (
                  <div className="panel-empty-state">
                    <CheckCircle2 size={36} className="text-emerald-500" />
                    <h4>لا توجد مدفوعات معلقة حالياً!</h4>
                    <p>جميع إثباتات التحويل الواردة تمت مراجعتها واعتمادها.</p>
                  </div>
                ) : (
                  <div className="pending-queue-list">
                    {recentAttempts.map((item) => (
                      <div
                        key={item.id}
                        className="queue-item-card"
                        onClick={() => navigate('/admin/payments')}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="queue-item-icon">
                          {item.paymentMethod === 'INSTAPAY' ? (
                            <Phone size={20} className="text-purple-600" />
                          ) : item.paymentMethod === 'WALLET' ? (
                            <CreditCard size={20} className="text-emerald-600" />
                          ) : (
                            <Building2 size={20} className="text-[#E5232A]" />
                          )}
                        </div>

                        <div className="queue-item-info">
                          <div className="queue-item-title">
                            <strong>{item.studentName || 'طالب'}</strong>
                            <span className="booking-code font-mono">
                              #{item.bookingCode || item.bookingGroupCode || (item.bookingId ? item.bookingId.slice(0, 7) : item.bookingGroupId ? item.bookingGroupId.slice(0, 7) : '—')}
                            </span>
                          </div>
                          <div className="queue-item-meta">
                            <span>{item.universityName || 'الجامعة'}</span>
                            <span>•</span>
                            <span>مقعد #{item.seatNumber || '—'}</span>
                            <span>•</span>
                            <span>من: {item.transferFrom || 'سداد فرع'}</span>
                          </div>
                        </div>

                        <div className="queue-item-amount">
                          <strong className="amount-text">{item.amount} ج.م</strong>
                          <span className="queue-item-btn">مراجعة ◄</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="panel-header-row">
                <div className="flex items-center gap-2">
                  <Ticket size={18} className="text-[#E5232A]" />
                  <h3>أحدث حجوزات الطلاب المسجلة</h3>
                </div>
                <Link to="/admin/bookings" className="panel-link">
                  <span>سجل الحجوزات والكلندر</span>
                  <ArrowLeft size={16} />
                </Link>
              </div>

              <div className="panel-body">
                {isLoading ? (
                  <p className="p-4 text-center text-slate-500 text-sm">جاري جلب الحجوزات...</p>
                ) : todayBookings.length === 0 ? (
                  <div className="panel-empty-state">
                    <CheckCircle2 size={36} className="text-emerald-500" />
                    <h4>لا توجد حجوزات جديدة اليوم حتى الآن</h4>
                    <p>يمكنك استعراض ومتابعة كافة المقاعد المحجوزة من قسم الحجوزات والكلندر.</p>
                  </div>
                ) : (
                  <div className="pending-queue-list">
                    {todayBookings.slice(0, 6).map((b) => (
                      <div
                        key={b.id}
                        className="queue-item-card"
                        onClick={() => navigate('/admin/bookings')}
                        role="button"
                        tabIndex={0}
                      >
                        <div className="queue-item-icon">
                          <Ticket size={20} className="text-[#E5232A]" />
                        </div>

                        <div className="queue-item-info">
                          <div className="queue-item-title">
                            <strong>{b.studentSnapshot?.fullName || (b as any).studentName || 'طالب'}</strong>
                            <span className="booking-code font-mono">
                              #{b.bookingCode || (b.id ? b.id.slice(0, 7) : '—')}
                            </span>
                          </div>
                          <div className="queue-item-meta">
                            <span>{b.universityName || 'الجامعة'}</span>
                            <span>•</span>
                            <span>مقعد #{b.seatNumber || '—'}</span>
                            <span>•</span>
                            <span>تاريخ: {b.tripDate || 'اليوم'}</span>
                          </div>
                        </div>

                        <div className="queue-item-amount">
                          <span
                            style={{
                              background: b.status === 'CONFIRMED' ? '#DCFCE7' : '#FEF3C7',
                              color: b.status === 'CONFIRMED' ? '#166534' : '#92400E',
                              padding: '4px 10px',
                              borderRadius: '8px',
                              fontSize: '12px',
                              fontWeight: '700',
                            }}
                          >
                            {b.status === 'CONFIRMED' ? 'مؤكد' : 'معلق'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Right Column: Quick Shortcuts & Fleet Status */}
        <div className="admin-dash-sidebar-col">
          {/* Quick Management Shortcuts */}
          <div className="admin-dash-panel">
            <div className="panel-header-row">
              <h3>{isOperator ? 'روابط التشغيل السريعة' : 'روابط الإدارة السريعة'}</h3>
            </div>
            <div className="admin-shortcuts-list">
              {!isOperator ? (
                <>
                  <Link to="/admin/payments" className="admin-shortcut-item">
                    <Clock size={18} className="text-purple-600" />
                    <div>
                      <strong>اعتماد المدفوعات</strong>
                      <span>فحص إثباتات التحويل والموافقة</span>
                    </div>
                    <ArrowLeft size={16} />
                  </Link>

                  <Link to="/admin/payment-settings" className="admin-shortcut-item">
                    <CreditCard size={18} className="text-emerald-600" />
                    <div>
                      <strong>إعدادات وسائل الدفع</strong>
                      <span>تعديل رقم إنستاباي والمحافظ والفرع</span>
                    </div>
                    <ArrowLeft size={16} />
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/admin/create-student" className="admin-shortcut-item">
                    <Ticket size={18} className="text-emerald-600" />
                    <div>
                      <strong>إنشاء حساب طالب (واتساب)</strong>
                      <span>إضافة طالب جديد وإرسال رسالة التسجيل</span>
                    </div>
                    <ArrowLeft size={16} />
                  </Link>

                  <Link to="/admin/students" className="admin-shortcut-item">
                    <Building2 size={18} className="text-sky-600" />
                    <div>
                      <strong>الطلاب والمسارات</strong>
                      <span>تعديل مسارات الانطلاق ومحطات الركوب</span>
                    </div>
                    <ArrowLeft size={16} />
                  </Link>
                </>
              )}

              <Link to="/admin/trips" className="admin-shortcut-item">
                <Bus size={18} className="text-[#E5232A]" />
                <div>
                  <strong>جدول الرحلات</strong>
                  <span>مواعيد الحافلات ومسارات الانطلاق</span>
                </div>
                <ArrowLeft size={16} />
              </Link>

              <Link to="/admin/bookings" className="admin-shortcut-item">
                <Ticket size={18} className="text-amber-600" />
                <div>
                  <strong>سجل الحجوزات والتذاكر</strong>
                  <span>استعراض كافة الحجوزات والركاب</span>
                </div>
                <ArrowLeft size={16} />
              </Link>
            </div>
          </div>

          {/* Operational Baseline Card */}
          <div className="admin-dash-panel fleet-card">
            <div className="flex items-start gap-3">
              <Bus size={32} className="text-[#E5232A] flex-shrink-0" />
              <div>
                <h4>أسطول الحافلات المجهزة</h4>
                <p className="text-xs text-slate-600 leading-relaxed mt-1">
                  حافلات مجهزة (50 راكباً) — تنطلق من محطة مصر وسيدي جابر بالإسكندرية مباشرة إلى بوابات الجامعات.
                </p>
                <div className="mt-2 text-xs font-semibold text-emerald-700">
                  {allTrips.length > 0
                    ? `إجمالي الرحلات المسجلة بالمنظومة: ${allTrips.length} رحلة`
                    : 'حالة التشغيل: نشط ومستقر'}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
